import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Copy, LinkBreak, Plugs } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { api } from "../../lib/api";
import { NetworkDot, errMsg, fmtDate } from "./socialShared";

const PROVIDERS = [
  { id: "linkedin", title: "LinkedIn (page entreprise)", nets: ["linkedin"], idLabel: "Client ID", secretLabel: "Client Secret", portal: "https://www.linkedin.com/developers/apps",
    help: "Créez une application, reliez-la à votre page entreprise et demandez le produit « Community Management API »." },
  { id: "meta", title: "Facebook + Instagram", nets: ["facebook", "instagram"], idLabel: "App ID", secretLabel: "App Secret", portal: "https://developers.facebook.com/apps/",
    help: "Créez une application Business avec « Facebook Login for Business ». Le compte Instagram doit être un compte pro relié à la page Facebook." },
  { id: "tiktok", title: "TikTok", nets: ["tiktok"], idLabel: "Client key", secretLabel: "Client secret", portal: "https://developers.tiktok.com/apps/",
    help: "Créez une application avec « Login Kit » et « Content Posting API » (Direct Post). Tant que TikTok n'a pas audité l'app, les vidéos sont publiées en privé." },
];

function ProviderCard({ p, settings, account, reload }) {
  const [cid, setCid] = useState(settings[p.id]?.client_id || "");
  const [secret, setSecret] = useState("");
  const uri = settings.redirect_uris[p.id];
  const save = async () => {
    try { await api.put("/social/settings", { [p.id]: { client_id: cid, client_secret: secret || null } }); setSecret(""); toast.success("Identifiants enregistrés"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const connect = async () => { try { window.location.href = (await api.get(`/social/oauth/${p.id}/start`)).data.url; } catch (e) { toast.error(errMsg(e)); } };
  const disconnect = async () => { if (!window.confirm("Déconnecter ce compte ?")) return; await api.delete(`/social/accounts/${p.id}`); reload(); };
  const setTarget = async (target_id) => { try { await api.put(`/social/accounts/${p.id}/target`, { target_id }); toast.success("Page choisie"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid={`provider-card-${p.id}`}>
      <div className="flex items-center gap-2">
        {p.nets.map((n) => <NetworkDot key={n} id={n} size={20} />)}
        <span className="font-semibold">{p.title}</span>
        {account?.connected
          ? <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`provider-${p.id}-connected`}><CheckCircle size={14} weight="fill" />{account.account_name}{account.expired ? " (expiré)" : ""}</span>
          : <span className="ml-auto text-xs text-muted-foreground" data-testid={`provider-${p.id}-disconnected`}>Non connecté</span>}
      </div>
      <p className="text-xs text-muted-foreground">{p.help} <a href={p.portal} target="_blank" rel="noreferrer" className="underline">Portail développeur</a></p>
      <div className="text-xs">
        <Label className="text-xs">Adresse de redirection à déclarer dans l'application</Label>
        <div className="mt-1 flex items-center gap-2 rounded-md border bg-muted px-2 py-1 font-mono text-[11px] break-all">
          <span className="flex-1" data-testid={`provider-${p.id}-redirect`}>{uri}</span>
          <button type="button" onClick={() => { navigator.clipboard.writeText(uri); toast.success("Copié"); }}><Copy size={14} /></button>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div><Label className="text-xs">{p.idLabel}</Label><Input value={cid} onChange={(e) => setCid(e.target.value)} data-testid={`provider-${p.id}-client-id`} /></div>
        <div><Label className="text-xs">{p.secretLabel}</Label><Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={settings[p.id]?.has_secret ? "•••••• (enregistré)" : ""} data-testid={`provider-${p.id}-secret`} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={save} data-testid={`provider-${p.id}-save`}>Enregistrer</Button>
        <Button size="sm" onClick={connect} disabled={!settings[p.id]?.has_secret || !settings[p.id]?.client_id} data-testid={`provider-${p.id}-connect`}><Plugs size={14} className="mr-1" />{account?.connected ? "Reconnecter" : "Connecter le compte"}</Button>
        {account?.connected && <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnect} data-testid={`provider-${p.id}-disconnect`}><LinkBreak size={14} className="mr-1" />Déconnecter</Button>}
      </div>
      {account?.connected && account.targets?.length > 0 && (
        <div className="text-xs">
          <Label className="text-xs">Page utilisée pour publier</Label>
          <select className="mt-1 block rounded-md border bg-background px-2 py-1 text-sm" value={account.target_id || ""} onChange={(e) => setTarget(e.target.value)} data-testid={`provider-${p.id}-target`}>
            {account.targets.map((t) => <option key={t.id} value={t.id}>{t.name}{t.ig_username ? ` (Instagram @${t.ig_username})` : p.id === "meta" ? " (sans Instagram)" : ""}</option>)}
          </select>
          {account.expires_at && <div className="mt-1 text-muted-foreground">Accès valable jusqu'au {fmtDate(account.expires_at)}</div>}
        </div>
      )}
    </div>
  );
}

function TechAccess({ settings, reload }) {
  const [ids, setIds] = useState(settings.allowed_tech_ids);
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const save = async () => { try { await api.put("/social/settings", { allowed_tech_ids: ids }); toast.success("Accès mis à jour"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="tech-access-card">
      <div className="font-semibold">Techniciens autorisés</div>
      <p className="text-xs text-muted-foreground">Ils peuvent rédiger et programmer des posts ; chaque post doit être validé par un admin avant de partir.</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {settings.technicians.map((t) => (
          <label key={t.id} className="flex items-center gap-2 text-sm">
            <Checkbox checked={ids.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`tech-access-${t.id}`} />{t.name}
          </label>
        ))}
        {!settings.technicians.length && <span className="text-sm text-muted-foreground">Aucun technicien actif.</span>}
      </div>
      <Button size="sm" onClick={save} data-testid="tech-access-save">Enregistrer les accès</Button>
    </div>
  );
}

export function SocialSettings({ accounts, onChanged }) {
  const [settings, setSettings] = useState(null);
  const load = () => api.get("/social/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!settings) return null;
  const reload = () => { load(); onChanged?.(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="social-settings">
      {PROVIDERS.map((p) => <ProviderCard key={p.id} p={p} settings={settings} account={accounts.find((a) => a.provider === p.id)} reload={reload} />)}
      <TechAccess settings={settings} reload={reload} />
    </div>
  );
}
